import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { IonContent, Platform } from '@ionic/angular';
import { SeoService } from 'src/app/services/seo.service';
import { environment } from 'src/environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { SEOSchema } from 'src/app/types/seo';
import {
  GlobalDataInterface,
  GlobalDataService,
} from 'src/app/services/global-data.service';
import { JsonLdService } from 'src/app/services/json-ld.service';
import { HeroInterface } from 'src/app/components/dynamic-html/nodes/hero/hero.component';
import { CinemaProvider, Movie } from 'src/app/types/movie';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { ExploreInterface } from 'src/app/components/dynamic-html/nodes/explore/explore.component';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
})
export class HomePage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  environment = environment;
  isBrowser: boolean = false;
  cordova: boolean = false;
  showToolbar: boolean = false;
  columnSize: number = 4;
  startIndex: number = 1;
  hero: HeroInterface;
  explore: ExploreInterface;

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: environment.environment_title,
    url: environment.environment_url,
  };

  slideOpts = {
    freeMode: false,
    pagination: false,
    breakpoints: {
      280: {
        slidesPerView: 1.2,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2.4,
        spaceBetween: 40,
      },
      1024: {
        slidesPerView: 3.33,
        spaceBetween: 40,
      },
    },
  };

  slideOptsMovies = {
    freeMode: false,
    pagination: false,
    breakpoints: {
      280: {
        slidesPerView: 2.2,
        spaceBetween: 15,
      },
      768: {
        slidesPerView: 'auto',
        spaceBetween: 30,
      },
    },
  };

  data: any;
  nowShowing: Movie[] = [];
  cinemaProvider: CinemaProvider;
  initialMallData: GlobalDataInterface;

  constructor(
    private API: ApiService,
    private route: ActivatedRoute,
    private platform: Platform,
    private seo: SeoService,
    private jsonLdService: JsonLdService,
    private globalData: GlobalDataService,
    @Inject(PLATFORM_ID) private platformId
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
    });
    this.globalData.dataChange.subscribe((globalData) => {
      if (globalData?.nowShowing) {
        this.nowShowing = globalData.nowShowing;
        this.cinemaProvider = globalData.cinemaProvider;
      }
      this.updateSEOData(globalData);
    });

    this.initialMallData = this.globalData.getGlobalData();
  }

  /**
   * Update page SEO & Schema data
   */
  updateSEOData(data) {
    if (data.social) {
      let social = [];

      if (data.social.facebookPageURL) social.push(data.social.facebookPageURL);
      if (data.social.youtubeChannelURL)
        social.push(data.social.youtubeChannelURL);
      if (data.social.instagramAccountURL)
        social.push(data.social.instagramAccountURL);
      if (data.social.twitterPageURL) social.push(data.social.twitterPageURL);
      if (data.social.linkedInProfileURL)
        social.push(data.social.linkedInProfileURL);
      if (data.social.pinterestURL) social.push(data.social.pinterestURL);

      if (social.length > 0) this.schema = { ...this.schema, sameAs: social };

      setTimeout(() => {
        this.jsonLdService.insertSchema(this.schema);
      }, 0);
    }
  }

  /**
   * Change header background color when scrolling
   */
  onScroll($event: CustomEvent) {
    if (this.isBrowser && $event && $event.detail && $event.detail.scrollTop) {
      const scrollTop = $event.detail.scrollTop;
      this.showToolbar = scrollTop >= 160;
    }
  }

  ngOnInit() {
    this.route.data.subscribe((response: any) => {
      const body = response.data.body?.blocks
        ? response.data.body?.blocks.filter((block) => {
            if (block.type === 'hero') {
              this.hero = block.data;
              return false;
            }
            if (block.type === 'footer_explore') {
              this.explore = block.data;
              return false;
            }

            return true;
          })
        : [];

      this.data = {
        ...response.data,
        body: { blocks: body },
      };

      if (this.data.featuredItems?.events) {
        if (this.data.featuredItems?.events.length == 2) {
          this.columnSize = 6;
          this.startIndex = 0;
        }
        if (this.data.featuredItems?.events.length == 3) {
          this.columnSize = 6;
          this.startIndex = 1;
        }
      }

      // Set Meta & Social tags
      if (!this.cordova) {
        let schemaData = { ...this.schema };

        // Title tag
        if (this.data.seo?.title) {
          this.seo.setTitle(this.data.seo.title);
        } else {
          this.seo.setTitle(this.data.title);
        }

        // Meta tags
        if (this.data.seo) {
          this.seo.setMeta(this.data.seo);
          this.seo.setRobotsMeta(this.data.seo);
        }

        // Social tags
        if (this.data.social) {
          this.seo.setSEOMeta(this.data.social);
        }

        // Schema image
        if (this.initialMallData.general.mallLogo) {
          schemaData = {
            ...schemaData,
            logo: this.initialMallData.general.mallLogo,
          };
        }

        // Author
        if (this.data.createdBy) {
          this.seo.setAuthor(this.data.createdBy);
        }

        // Canonical URL
        if (this.data.seo?.canonical_url) {
          this.seo.setCanonicalURL(this.data.seo);
        }

        this.schema = this.schema = { ...this.schema, ...schemaData };
        this.jsonLdService.insertSchema(this.schema);
      }
    });

    const initialMallData = this.globalData.getGlobalData();
    if (initialMallData?.nowShowing) {
      this.nowShowing = initialMallData.nowShowing;
      this.cinemaProvider = initialMallData.cinemaProvider;
      this.updateSEOData(initialMallData);
    } else if (
      isPlatformBrowser(this.platformId) &&
      initialMallData.cinemaProvider &&
      initialMallData.cinemaProvider.moviesEnabled
    ) {
      this.API.GET(
        environment.cdn_url +
          'movies/' +
          (initialMallData?.cinemaProvider?.name === 'Nu Metro'
            ? 'moviesNuMetro.json'
            : 'moviesSK.json')
      ).subscribe({
        next: (movies) => {
          if (movies.data.sessions) this.globalData.setMovieData(movies.data);

          setTimeout(() => {
            this.nowShowing = this.globalData.getGlobalData().nowShowing;
          }, 0);
        },
        error: () => {},
      });
    }
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
    this.showToolbar = false;
  }
}